import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'classnames'
import { Helmet } from '@dr.pogodin/react-helmet'
import { useLocation, useNavigate } from 'react-router'
import { YMaps } from '@pbe/react-yandex-maps'
import { ServiceSiteType, type Media, type SalonPage as SalonPageData } from 'api/api.types'
import smallMap from 'assets/small-map.png'
import { parseHtml } from 'utils/htmlParserWithCustomLinks'
import { getYoutubeEmbedUrl, getYoutubeId, getYoutubePreviewUrl } from 'utils/youtube'
import { SalonInfo } from 'components/PageElements/SalonInfo/SalonInfo'
import { SalonsMap } from 'components/PageElements/Salons/SalonsMap'
import { MainMenu } from 'components/PageElements/MainMenu/MainMenu'
import { ImageLoader } from 'components/Utility/ImageLoader/ImageLoader'
import { Arrow } from 'components/Utility/Arrow/Arrow'
import { Picture } from 'components/Utility/Media/Picture'
import { SalonMetrika } from './SalonMetrika'
import styles from './SalonPage.module.scss'

/** ?category= values of the services menu; the menu writes them back to the URL */
const CATEGORY_BY_NAME: Record<string, ServiceSiteType> = {
  complex: ServiceSiteType.complex,
  manicure: ServiceSiteType.manicure,
  pedicure: ServiceSiteType.pedicure,
}

/** The gallery ends with the salon map */
type GalleryItem = Media | { type: 'map' }

type ScrollState = {
  atLeft: boolean,
  atRight: boolean,
  position: number,
}

type SalonPageProps = {
  page: SalonPageData,
}

export const SalonPage: React.FC<SalonPageProps> = ({ page }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const vars = page.templateVars.pageVars
  const { salon } = vars
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [thumbsScrollState, setThumbsScrollState] = useState<ScrollState>({ atLeft: true, atRight: false, position: 0 })
  const thumbsContainerRef = useRef<HTMLDivElement>(null)
  const thumbsRef = useRef<HTMLDivElement>(null)

  const category = useMemo(() => new URLSearchParams(location.search).get('category'), [location.search])
  // Unknown values fall back to the menu's own default
  const defaultMenuCategory = category ? CATEGORY_BY_NAME[category] : ServiceSiteType.manicure

  const gallery = useMemo<GalleryItem[]>(() => [...(vars.images ?? []), { type: 'map' }], [vars.images])

  const shareImage = useMemo(() => {
    const image = vars.images?.find((i) => i.type === 'image')
    return image?.image?.find((i) => i.type === 'fullscreen-mobile') ?? null
  }, [vars.images])

  const handleArrowClick = useCallback((direction: number) => {
    if (!thumbsContainerRef.current || !thumbsRef.current) {
      return
    }
    const containerWidth = thumbsContainerRef.current.clientWidth
    const thumbsWidth = thumbsRef.current.clientWidth
    setThumbsScrollState((state) => {
      const nextPosition = Math.max(
        containerWidth - thumbsWidth,
        Math.min(0, state.position - direction * containerWidth),
      )
      return {
        atLeft: nextPosition === 0,
        atRight: nextPosition === containerWidth - thumbsWidth,
        position: nextPosition,
      }
    })
  }, [])

  const updateCategory = useCallback((id: number) => {
    const name = Object.keys(CATEGORY_BY_NAME).find((key) => CATEGORY_BY_NAME[key] === id) ?? ''
    navigate({ search: `category=${name}` })
  }, [navigate])

  useEffect(() => {
    if (thumbsContainerRef.current && thumbsRef.current) {
      setThumbsScrollState({
        atLeft: true,
        atRight: thumbsContainerRef.current.clientWidth >= thumbsRef.current.clientWidth,
        position: 0,
      })
    }
  }, [])

  const selectedImage = gallery[selectedImageIndex]
  let currentSlide: React.ReactNode = null
  if (selectedImage?.type === 'image') {
    currentSlide = <Picture
      image={selectedImage.image}
      desktopType={'fullscreen-desktop'}
      mobileType={'fullscreen-mobile'}
      className={styles.currentSlide}
      imageClassName={styles.currentSlideImage}
      alt={selectedImage.imageAlt}
      preload={true}
    />
  } else if (selectedImage?.type === 'youtube') {
    currentSlide = <div className={styles.currentSlide}>
      <iframe
        allow="autoplay; encrypted-media"
        title={selectedImage.title}
        className={styles.currentSlideImage}
        src={getYoutubeEmbedUrl(getYoutubeId(selectedImage.youtubeLink))}
      />
    </div>
  } else if (selectedImage?.type === 'map') {
    currentSlide = <YMaps version={'2.1'}>
      <SalonsMap lazy={false} withBalloons={false} salon={vars} />
    </YMaps>
  }

  const renderThumb = (item: GalleryItem, index: number) => {
    const className = clsx([styles.thumb, { [styles.active]: index === selectedImageIndex }])
    const onClick = () => setSelectedImageIndex(index)
    if (item.type === 'image') {
      return <Picture
        key={index}
        image={item.image}
        desktopType={'preview'}
        mobileType={'preview'}
        className={className}
        imageClassName={styles.thumbImage}
        alt={item.imageAlt}
        onClick={onClick}
      />
    }
    if (item.type === 'youtube') {
      return <ImageLoader
        key={index}
        src={getYoutubePreviewUrl(getYoutubeId(item.youtubeLink))}
        className={className}
        imageClassName={styles.thumbImage}
        onClick={onClick}
      />
    }
    return <ImageLoader
      key={index}
      src={smallMap}
      alt={'Cалон маникюра "Пальчики" на карте'}
      className={className}
      imageClassName={styles.thumbImage}
      onClick={onClick}
    />
  }

  return (
    <div className={styles.container}>
      <Helmet>
        <meta property={'og:type'} content={'website'} />
        {shareImage && <meta property={'og:image'} content={shareImage.link} />}
        {shareImage && <meta property={'og:image:width'} content={shareImage.resolution.split('x')[0]} />}
        {shareImage && <meta property={'og:image:height'} content={shareImage.resolution.split('x')[1]} />}
      </Helmet>
      <SalonMetrika salonId={salon.id} />
      <div className={styles.mainInfo}>
        <div ref={thumbsContainerRef} className={styles.slider}>
          {currentSlide}
          <div className={styles.thumbsContainer}>
            <div
              className={clsx([styles.thumbsScroller, { [styles.hidden]: thumbsScrollState.atLeft }])}
              data-direction={'left'}
              onClick={() => handleArrowClick(-1)}
            >
              <Arrow bigOne direction={'left'} />
            </div>
            <div
              ref={thumbsRef}
              className={styles.thumbnails}
              style={{ transform: `translateX(${thumbsScrollState.position}px)`, transition: 'transform .1s linear' }}
            >
              {gallery.map(renderThumb)}
            </div>
            <div
              className={clsx([styles.thumbsScroller, { [styles.hidden]: thumbsScrollState.atRight }])}
              data-direction={'right'}
              onClick={() => handleArrowClick(1)}
            >
              <Arrow bigOne direction={'right'} />
            </div>
          </div>
        </div>
        <div className={styles.titleContainer}>
          <h1 className={styles.title}>{page.title}</h1>
        </div>
        <div className={styles.salon}>
          <div className={styles.info}>
            <SalonInfo meta={page} salon={salon} />
          </div>
        </div>
      </div>
      <MainMenu onlyMainCategories defaultCategory={defaultMenuCategory} onChangeCategory={updateCategory} />
      <div className={styles.additional}>
        <div className={styles.additionalTitle}>
          Дополнительная информация
        </div>
        <div className={clsx([styles.props, styles.description])}>
          <div className={styles.value} itemProp="description">
            {parseHtml(vars.description)}
          </div>
        </div>
      </div>
    </div>
  )
}
